/**
 * HHMS Parties Module — API Service (ss* schema)
 * Base: /api/parties-module/v1
 *
 * Covers every endpoint defined in §6 of PARTIES_MODULE_TECHNICAL_DESIGN.
 * Organised by bounded context matching the backend module layout.
 */
import axios from 'axios';
import { refreshAccessToken } from './refreshClient';
import { API_ORIGIN } from './apiBase';

// Origin only ('' in production = same origin). Paths are appended below.
const BASE_URL = API_ORIGIN;

const ssApi = axios.create({ baseURL: `${BASE_URL}/api/parties-module/v1`, timeout: 15000 });

// ── Optimistic-concurrency cache ─────────────────────────────────────────────
// The backend stamps every read with an ETag derived from `sys_timestamp`.
// We capture it from GET responses keyed by URL, then automatically attach
// `If-Match: <etag>` to the next PUT/PATCH/DELETE for the same URL.  On a
// 412/428/409 the user is prompted to reload (handled by the surrounding
// component via the `ssApiOnConflict` event).
const _etagCache = new Map();

const _etagKey = (config) => {
  // Strip trailing slash and query so `/parties/42` and `/parties/42/` share
  // the same key — the GET that hydrated the row probably matched either.
  const url = (config.baseURL || '') + (config.url || '');
  return url.replace(/\/+$/, '');
};

ssApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // Attach If-Match for unsafe verbs when we have a cached ETag.
  const method = (config.method || 'get').toLowerCase();
  if (['put', 'patch', 'delete'].includes(method)) {
    const cached = _etagCache.get(_etagKey(config));
    if (cached && !config.headers['If-Match']) {
      config.headers['If-Match'] = cached;
    }
  }
  return config;
});

/**
 * Normalise any FastAPI error payload to a human-readable string.
 * 422 validation errors arrive as `detail: [{type, loc, msg, input}, …]` —
 * rendering that array as a React child crashes the whole tree (and the
 * crash-recovery remount is what makes inputs lose focus per keystroke).
 */
export function formatApiError(err) {
  const detail = err?.response?.data?.detail ?? err?.response?.data;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        if (typeof d === 'string') return d;
        const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== 'body').join('.') : '';
        return field ? `${field}: ${d?.msg || 'invalid'}` : (d?.msg || JSON.stringify(d));
      })
      .join(' · ');
  }
  if (detail && typeof detail === 'object') {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return err?.message || 'Request failed';
}

ssApi.interceptors.response.use(
  (res) => {
    // Cache ETag from GET responses so subsequent updates send If-Match.
    if ((res.config?.method || 'get').toLowerCase() === 'get') {
      const etag = res.headers?.etag || res.headers?.ETag;
      if (etag) _etagCache.set(_etagKey(res.config), etag);
    }
    return res;
  },
  async (err) => {
    const original = err.config;

    // Rewrite non-string error details in place so every existing
    // `ex.response?.data?.detail` call site renders a safe string.
    if (err.response?.data && typeof err.response.data.detail !== 'string') {
      try { err.response.data.detail = formatApiError(err); } catch { /* noop */ }
    }

    // Optimistic-concurrency conflict — surface to the UI once.
    const status = err.response?.status;
    if (status === 409 || status === 412 || status === 428) {
      try {
        window.dispatchEvent(new CustomEvent('ssApiOnConflict', {
          detail: {
            status,
            url:    original?.url,
            method: original?.method,
            data:   err.response?.data,
          },
        }));
      } catch {/* noop in non-DOM envs */}
    }

    if (status === 401 && !original._retry) {
      original._retry = true;
      try {
        // Single-flight refresh shared with api.js so concurrent 401s don't
        // double-spend the single-use refresh token. The shared helper also
        // persists the rotated refresh_token (this client previously dropped it).
        const newToken = await refreshAccessToken(`${BASE_URL}/api/v1/auth/refresh`);
        if (newToken) {
          original.headers.Authorization = `Bearer ${newToken}`;
          return ssApi(original);
        }
      } catch {
        localStorage.clear();
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

/** Manually drop a cached ETag — call after a successful save so the next
 *  GET re-hydrates instead of using the stale token. */
export const ssApiClearEtag = (path) => {
  if (!path) { _etagCache.clear(); return; }
  // Must mirror the axios baseURL exactly (incl. same-origin '' in prod) so the
  // cache key matches what the request interceptor stored.
  const fullKey = `${BASE_URL}/api/parties-module/v1` + path;
  _etagCache.delete(fullKey.replace(/\/+$/, ''));
};

const unwrap = (res) => res.data;

// ─────────────────────────────────────────────────────────────────────────────
// §6.1 + §6.2  Identity — Parties & child entities
// ─────────────────────────────────────────────────────────────────────────────
export const ssPartyAPI = {
  // Core CRUD
  list:         (params)       => ssApi.get('/parties', { params }).then(unwrap),
  get:          (id)           => ssApi.get(`/parties/${id}`).then(unwrap),
  // getFull is defined below with PII reveal options (kept overload-friendly:
  // calling with just (id) returns the redacted bundle).
  create:       (d)            => ssApi.post('/parties', d).then(unwrap),
  update:       (id, d)        => ssApi.put(`/parties/${id}`, d).then(unwrap),
  close:        (id, reason)   => ssApi.delete(`/parties/${id}`, { params: { reason } }).then(unwrap),
  restore:      (id)           => ssApi.post(`/parties/${id}/restore`).then(unwrap),

  // Image
  uploadImage:  (id, file)     => {
    const fd = new FormData(); fd.append('file', file);
    return ssApi.post(`/parties/${id}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(unwrap);
  },
  getImageUrl:  (id)           => `${BASE_URL}/api/parties-module/v1/parties/${id}/image`,

  // Custom data
  patchCustomdata: (id, d)     => ssApi.patch(`/parties/${id}/customdata`, d).then(unwrap),

  // Deduplication & merge
  duplicates:   (id)           => ssApi.get(`/parties/${id}/duplicates`).then(unwrap),
  merge:        (d)            => ssApi.post('/parties/merge', d).then(unwrap),
  mergeDryRun:  (d)            => ssApi.post('/parties/merge', { ...d, dry_run: true }).then(unwrap),

  // Merge log + 30-day unmerge window (§14.3)
  listMerges:   (params = {})  => ssApi.get('/parties/merges', { params }).then(unwrap),
  unmerge:      (mergeLogId)   => ssApi.post(`/parties/merge/${mergeLogId}/unmerge`).then(unwrap),

  // PII reveal (§12.4) — pass `reveal=true` to the /full endpoint to receive
  // the un-redacted bundle.  The server emits a `compliance.pii_revealed.v1`
  // event so every reveal is auditable.
  getFull:      (id, opts = {}) => {
    const params = opts.reveal ? { reveal: true } : {};
    const headers = opts.purpose ? { 'X-Purpose-Of-Use': opts.purpose } : {};
    return ssApi.get(`/parties/${id}/full`, { params, headers }).then(unwrap);
  },

  // Grouped global search (top-bar typeahead)
  globalSearch: (q, limit = 8) => ssApi.get('/search', { params: { q, limit } }).then(unwrap),

  // Party audit history — optional params e.g. { limit: 10 }
  auditHistory: (id, params)   => ssApi.get(`/parties/${id}/audit`, { params }).then(unwrap),

  // Addresses
  listAddresses:   (id)        => ssApi.get(`/parties/${id}/addresses`).then(unwrap),
  addAddress:      (id, d)     => ssApi.post(`/parties/${id}/addresses`, d).then(unwrap),
  updateAddress:   (id, aid, d)=> ssApi.put(`/parties/${id}/addresses/${aid}`, d).then(unwrap),
  removeAddress:   (id, aid)   => ssApi.delete(`/parties/${id}/addresses/${aid}`).then(unwrap),
  setMainAddress:  (id, aid)   => ssApi.post(`/parties/${id}/addresses/${aid}/set-main`).then(unwrap),

  // Contacts
  listContacts:    (id)        => ssApi.get(`/parties/${id}/contacts`).then(unwrap),
  addContact:      (id, d)     => ssApi.post(`/parties/${id}/contacts`, d).then(unwrap),
  updateContact:   (id, cid, d)=> ssApi.put(`/parties/${id}/contacts/${cid}`, d).then(unwrap),
  removeContact:   (id, cid)   => ssApi.delete(`/parties/${id}/contacts/${cid}`).then(unwrap),
  verifyContact:   (id, cid, token) => ssApi.post(`/parties/${id}/contacts/${cid}/verify`, { token }).then(unwrap),

  // Identifiers
  listIdentifiers:    (id)         => ssApi.get(`/parties/${id}/identifiers`).then(unwrap),
  addIdentifier:      (id, d)      => ssApi.post(`/parties/${id}/identifiers`, d).then(unwrap),
  updateIdentifier:   (id, iid, d) => ssApi.put(`/parties/${id}/identifiers/${iid}`, d).then(unwrap),
  deleteIdentifier:   (id, iid)    => ssApi.delete(`/parties/${id}/identifiers/${iid}`).then(unwrap),
  setMainIdentifier:  (id, iid)    => ssApi.post(`/parties/${id}/identifiers/${iid}/set-main`).then(unwrap),
  searchByIdentifier: (typeId, value)=> ssApi.get('/identifiers/search', { params: { type_id: typeId, value } }).then(unwrap),

  // Business (party) roles
  listPartyRoles:  (id)        => ssApi.get(`/parties/${id}/party-roles`).then(unwrap),
  assignPartyRole: (id, d)     => ssApi.post(`/parties/${id}/party-roles`, d).then(unwrap),
  revokePartyRole: (id, aid)   => ssApi.delete(`/parties/${id}/party-roles/${aid}`).then(unwrap),

  // Relationships
  listRelationships: (id, params) => ssApi.get(`/parties/${id}/relationships`, { params }).then(unwrap),
};

// ─────────────────────────────────────────────────────────────────────────────
// §6.2 Lookup tables — address types, contact media, identifier types
//      (full CRUD for admin)
// ─────────────────────────────────────────────────────────────────────────────
export const ssLookupAPI = {
  // Address types
  listAddressTypes:    ()       => ssApi.get('/address-types').then(unwrap),
  createAddressType:   (d)      => ssApi.post('/address-types', d).then(unwrap),
  updateAddressType:   (id, d)  => ssApi.put(`/address-types/${id}`, d).then(unwrap),
  deleteAddressType:   (id)     => ssApi.delete(`/address-types/${id}`).then(unwrap),

  // Contact media channels
  listContactMedia:    ()       => ssApi.get('/contact-media').then(unwrap),
  createContactMedia:  (d)      => ssApi.post('/contact-media', d).then(unwrap),
  updateContactMedia:  (id, d)  => ssApi.put(`/contact-media/${id}`, d).then(unwrap),
  deleteContactMedia:  (id)     => ssApi.delete(`/contact-media/${id}`).then(unwrap),

  // Contact media subtypes
  listContactMediaTypes:  ()       => ssApi.get('/contact-media-types').then(unwrap),
  createContactMediaType: (d)      => ssApi.post('/contact-media-types', d).then(unwrap),
  updateContactMediaType: (id, d)  => ssApi.put(`/contact-media-types/${id}`, d).then(unwrap),
  deleteContactMediaType: (id)     => ssApi.delete(`/contact-media-types/${id}`).then(unwrap),

  // Identifier types
  listIdentifierTypes:    ()       => ssApi.get('/identifier-types').then(unwrap),
  createIdentifierType:   (d)      => ssApi.post('/identifier-types', d).then(unwrap),
  updateIdentifierType:   (id, d)  => ssApi.put(`/identifier-types/${id}`, d).then(unwrap),
  deleteIdentifierType:   (id)     => ssApi.delete(`/identifier-types/${id}`).then(unwrap),

  // Business party roles
  listPartyRoles:    ()       => ssApi.get('/party-roles').then(unwrap),
  createPartyRole:   (d)      => ssApi.post('/party-roles', d).then(unwrap),
  updatePartyRole:   (id, d)  => ssApi.put(`/party-roles/${id}`, d).then(unwrap),
  deletePartyRole:   (id)     => ssApi.delete(`/party-roles/${id}`).then(unwrap),

  // Relationship types
  listRelTypes:      ()       => ssApi.get('/relationship-types').then(unwrap),
  createRelType:     (d)      => ssApi.post('/relationship-types', d).then(unwrap),
  updateRelType:     (id, d)  => ssApi.put(`/relationship-types/${id}`, d).then(unwrap),
  deleteRelType:     (id)     => ssApi.delete(`/relationship-types/${id}`).then(unwrap),

  // ── Backward-compatible aliases ────────────────────────────────────────────
  addressTypes:    () => ssApi.get('/address-types').then(unwrap),
  contactMedia:    () => ssApi.get('/contact-media').then(unwrap),
  identifierTypes: () => ssApi.get('/identifier-types').then(unwrap),
  partyRoles:      () => ssApi.get('/party-roles').then(unwrap),
  relTypes:        () => ssApi.get('/relationship-types').then(unwrap),
};

// ─────────────────────────────────────────────────────────────────────────────
// §6.3 Companies & Org Units
// ─────────────────────────────────────────────────────────────────────────────
export const ssCompanyAPI = {
  list:       ()           => ssApi.get('/companies').then(unwrap),
  tree:       ()           => ssApi.get('/companies/tree').then(unwrap),
  get:        (id)         => ssApi.get(`/companies/${id}`).then(unwrap),
  create:     (d)          => ssApi.post('/companies', d).then(unwrap),
  update:     (id, d)      => ssApi.put(`/companies/${id}`, d).then(unwrap),
  activate:   (id)         => ssApi.post(`/companies/${id}/activate`).then(unwrap),
  deactivate: (id)         => ssApi.post(`/companies/${id}/deactivate`).then(unwrap),
  uploadLogo: (id, file)   => {
    const fd = new FormData(); fd.append('file', file);
    return ssApi.put(`/companies/${id}/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(unwrap);
  },
};

export const ssOrgUnitAPI = {
  list:          ()              => ssApi.get('/org-units').then(unwrap),
  listForCompany:(companyId)     => ssApi.get('/org-units', { params: { company_id: companyId } }).then(unwrap),
  tree:          (companyId)     => ssApi.get('/org-units/tree', { params: { company_id: companyId } }).then(unwrap),
  get:           (id)            => ssApi.get(`/org-units/${id}`).then(unwrap),
  create:        (d)             => ssApi.post('/org-units', d).then(unwrap),
  update:        (id, d)         => ssApi.put(`/org-units/${id}`, d).then(unwrap),
  deactivate:    (id)            => ssApi.post(`/org-units/${id}/deactivate`).then(unwrap),
  move:          (id, parentId)  => ssApi.post(`/org-units/${id}/move`, { new_parent_id: parentId }).then(unwrap),
  listUsers:     (id)            => ssApi.get(`/org-units/${id}/users`).then(unwrap),
  assignUser:    (id, userId)    => ssApi.post(`/org-units/${id}/users/${userId}`).then(unwrap),
  removeUser:    (id, userId)    => ssApi.delete(`/org-units/${id}/users/${userId}`).then(unwrap),
};

// ─────────────────────────────────────────────────────────────────────────────
// §6.4 + §6.5 Auth & Users
// ─────────────────────────────────────────────────────────────────────────────
export const ssUserAPI = {
  // User CRUD
  list:      ()        => ssApi.get('/users').then(unwrap),
  get:       (id)      => ssApi.get(`/users/${id}`).then(unwrap),
  create:    (d)       => ssApi.post('/users', d).then(unwrap),
  update:    (id, d)   => ssApi.put(`/users/${id}`, d).then(unwrap),
  getMe:     ()        => ssApi.get('/users/me').then(unwrap),
  updateMe:  (d)       => ssApi.put('/users/me', d).then(unwrap),
  getOrgUnits: (id)    => ssApi.get(`/users/${id}/org-units`).then(unwrap),

  // Enable / disable / unlock
  enable:    (id)      => ssApi.post(`/users/${id}/enable`).then(unwrap),
  disable:   (id, d)   => ssApi.post(`/users/${id}/disable`, d).then(unwrap),
  unlock:    (id)      => ssApi.post(`/users/${id}/unlock`).then(unwrap),

  // Registration (admin — creates Party + User atomically)
  register:  (d)       => ssApi.post('/auth/register', d).then(unwrap),

  // Password management
  changePassword:      (d)   => ssApi.post('/auth/password/change', d).then(unwrap),
  resetPasswordInit:   (d)   => ssApi.post('/auth/password/reset/init', d).then(unwrap),
  resetPasswordConfirm:(d)   => ssApi.post('/auth/password/reset/confirm', d).then(unwrap),
  forceRotatePassword: (id)  => ssApi.post(`/auth/password/force-rotate/${id}`).then(unwrap),

  // 2FA
  enable2fa:   (id)    => ssApi.post('/auth/2fa/enable', { user_id: id }).then(unwrap),
  disable2fa:  (id)    => ssApi.post('/auth/2fa/disable', { user_id: id }).then(unwrap),
  get2faQr:    (id)    => ssApi.get('/auth/2fa/qrcode', { params: { user_id: id } }).then(unwrap),
  verify2fa:   (d)     => ssApi.post('/auth/2fa/verify', d).then(unwrap),

  // Sessions
  listSessions:    (id)    => ssApi.get(`/auth/sessions/user/${id}`).then(unwrap),
  listMySessions:  ()      => ssApi.get('/auth/sessions').then(unwrap),
  deleteSession:   (sid)   => ssApi.delete(`/auth/sessions/${sid}`).then(unwrap),
  revokeAll:       (id)    => ssApi.post(`/auth/sessions/revoke-all/${id}`).then(unwrap),

  // Backward-compat alias
  sessions: (id) => ssApi.get(`/auth/sessions/user/${id}`).then(unwrap),

  // Claims (OIDC-style attributes)
  listClaims:    (id)       => ssApi.get(`/users/${id}/claims`).then(unwrap),
  addClaim:      (id, d)    => ssApi.post(`/users/${id}/claims`, d).then(unwrap),
  removeClaim:   (id, cid)  => ssApi.delete(`/users/${id}/claims/${cid}`).then(unwrap),

  // External SSO logins
  listExternalLogins:   (id)       => ssApi.get(`/users/${id}/external-logins`).then(unwrap),
  removeExternalLogin:  (id, lid)  => ssApi.delete(`/users/${id}/external-logins/${lid}`).then(unwrap),

  // Impersonation (admin only)
  impersonate: (id) => ssApi.post(`/auth/impersonate/${id}`).then(unwrap),

  // Break-Glass emergency access
  breakglass: (d) => ssApi.post('/auth/breakglass', d).then(unwrap),
};

// ─────────────────────────────────────────────────────────────────────────────
// §6.6 Authz — Roles, Permissions, Security Objects
// ─────────────────────────────────────────────────────────────────────────────
export const ssRoleAPI = {
  // Role CRUD
  list:    ()        => ssApi.get('/roles').then(unwrap),
  create:  (d)       => ssApi.post('/roles', d).then(unwrap),
  update:  (id, d)   => ssApi.put(`/roles/${id}`, d).then(unwrap),
  delete:  (id)      => ssApi.delete(`/roles/${id}`).then(unwrap),

  // Role ↔ User assignments
  assignUser:     (uid, d)   => ssApi.post(`/users/${uid}/roles`, d).then(unwrap),
  revokeUser:     (uid, rid) => ssApi.delete(`/users/${uid}/roles/${rid}`).then(unwrap),
  userRoles:      (uid)      => ssApi.get(`/users/${uid}/roles`).then(unwrap),
  listRoleUsers:  (rid)      => ssApi.get(`/roles/${rid}/users`).then(unwrap),

  // Role permissions (ssroles_permissions)
  listRolePerms:  (rid)      => ssApi.get(`/roles/${rid}/permissions`).then(unwrap),
  grantPerm:      (rid, d)   => ssApi.post(`/roles/${rid}/permissions`, d).then(unwrap),
  revokePerm:     (rid, pid) => ssApi.delete(`/roles/${rid}/permissions/${pid}`).then(unwrap),

  // User permission overrides (ssusers_permissions)
  listUserPermsOverrides:  (uid)       => ssApi.get(`/users/${uid}/permissions`).then(unwrap),
  grantUserPermOverride:   (uid, d)    => ssApi.post(`/users/${uid}/permissions`, d).then(unwrap),
  revokeUserPermOverride:  (uid, pid)  => ssApi.delete(`/users/${uid}/permissions/${pid}`).then(unwrap),

  // Effective permissions (reads ssvw_userspermissions view)
  effectivePerms: (uid) => ssApi.get(`/users/${uid}/permissions/effective`).then(unwrap),

  // Permission check
  checkPerm: (d) => ssApi.post('/permissions/check', d).then(unwrap),
};

// Security catalog (sssecurityobjects + sssecurityobjects_methods)
export const ssSecurityAPI = {
  // Objects
  listObjects:   ()           => ssApi.get('/security/objects').then(unwrap),
  createObject:  (d)          => ssApi.post('/security/objects', d).then(unwrap),
  updateObject:  (id, d)      => ssApi.put(`/security/objects/${id}`, d).then(unwrap),
  deleteObject:  (id)         => ssApi.delete(`/security/objects/${id}`).then(unwrap),

  // Methods on a security object
  listMethods:   (oid)        => ssApi.get(`/security/objects/${oid}/methods`).then(unwrap),
  createMethod:  (oid, d)     => ssApi.post(`/security/objects/${oid}/methods`, d).then(unwrap),
  updateMethod:  (oid, mid, d)=> ssApi.put(`/security/objects/${oid}/methods/${mid}`, d).then(unwrap),
  deleteMethod:  (oid, mid)   => ssApi.delete(`/security/objects/${oid}/methods/${mid}`).then(unwrap),

  // Re-seed catalog from annotated code
  rescan:        ()           => ssApi.post('/security/rescan').then(unwrap),
};

// ─────────────────────────────────────────────────────────────────────────────
// §6.7 Relationships — Party Roles & Party Relationships
// ─────────────────────────────────────────────────────────────────────────────
export const ssRelAPI = {
  // Relationship CRUD
  create:  (d)       => ssApi.post('/relationships', d).then(unwrap),
  get:     (id)      => ssApi.get(`/relationships/${id}`).then(unwrap),
  update:  (id, d)   => ssApi.put(`/relationships/${id}`, d).then(unwrap),
  close:   (id, d)   => ssApi.post(`/relationships/${id}/close`, d).then(unwrap),
  reopen:  (id)      => ssApi.post(`/relationships/${id}/reopen`).then(unwrap),

  // Graph traversal shortcuts
  caregiverPatients: (pid) => ssApi.get(`/caregivers/${pid}/patients`).then(unwrap),
  patientCaregivers: (pid) => ssApi.get(`/patients/${pid}/caregivers`).then(unwrap),
  patientFamily:     (pid) => ssApi.get(`/patients/${pid}/family`).then(unwrap),
  patientInsurers:   (pid) => ssApi.get(`/patients/${pid}/insurers`).then(unwrap),

  // Visibility — party IDs the current user can see
  visibilityPatients: () => ssApi.get('/visibility/patients').then(unwrap),

  // List relationships for a party (forwards to /parties/{id}/relationships)
  listForParty: (pid, params) => ssApi.get(`/parties/${pid}/relationships`, { params }).then(unwrap),
};

// ─────────────────────────────────────────────────────────────────────────────
// Relationship graph (for the UI visualization)
// ─────────────────────────────────────────────────────────────────────────────
export const ssGraphAPI = {
  egoNetwork: (partyId, depth = 1, activeOnly = true) =>
    ssApi.get(`/parties/${partyId}/graph`, {
      params: { depth, active_only: activeOnly },
    }).then(unwrap),
};

// ─────────────────────────────────────────────────────────────────────────────
// Documents — party document attachments + types lookup
// ─────────────────────────────────────────────────────────────────────────────
export const ssDocumentAPI = {
  // Document types
  listTypes:     ()             => ssApi.get('/document-types').then(unwrap),
  createType:    (d)            => ssApi.post('/document-types', d).then(unwrap),
  updateType:    (id, d)        => ssApi.put(`/document-types/${id}`, d).then(unwrap),

  // Party documents
  listForParty:  (partyId, params) =>
    ssApi.get(`/parties/${partyId}/documents`, { params }).then(unwrap),
  upload:        (partyId, file, meta = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    Object.entries(meta).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') fd.append(k, v);
    });
    return ssApi
      .post(`/parties/${partyId}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(unwrap);
  },
  updateMeta:    (id, d)        => ssApi.put(`/documents/${id}`, d).then(unwrap),
  remove:        (id)           => ssApi.delete(`/documents/${id}`).then(unwrap),
  downloadUrl:   (id)           => `${BASE_URL}/api/parties-module/v1/documents/${id}/download`,
};

// ─────────────────────────────────────────────────────────────────────────────
// §6.8 Audit
// ─────────────────────────────────────────────────────────────────────────────
export const ssAuditAPI = {
  events:      (params)      => ssApi.get('/audit/events', { params }).then(unwrap),
  tables:      ()            => ssApi.get('/audit/tables').then(unwrap),
  toggleTable: (table, d)   => ssApi.put(`/audit/tables/${table}`, d).then(unwrap),
};

// ─────────────────────────────────────────────────────────────────────────────
// §6.9 Navigation
// ─────────────────────────────────────────────────────────────────────────────
export const ssNavigationAPI = {
  // Permission-filtered tree for current user
  tree:            ()               => ssApi.get('/navigation/tree').then(unwrap),

  // Admin CRUD on items and structures
  createItem:      (d)              => ssApi.post('/navigation/items', d).then(unwrap),
  updateItem:      (id, d)          => ssApi.put(`/navigation/items/${id}`, d).then(unwrap),
  deleteItem:      (id)             => ssApi.delete(`/navigation/items/${id}`).then(unwrap),
  listItems:       ()               => ssApi.get('/navigation/items').then(unwrap),

  createStructure: (d)              => ssApi.post('/navigation/structures', d).then(unwrap),
  updateStructure: (id, d)          => ssApi.put(`/navigation/structures/${id}`, d).then(unwrap),
  deleteStructure: (id)             => ssApi.delete(`/navigation/structures/${id}`).then(unwrap),
  listStructures:  ()               => ssApi.get('/navigation/structures').then(unwrap),

  // Per-user favorites — backend requires `user_id` as a query param
  addFavorite:     (structureId, userId) => ssApi.post(`/navigation/favorites/${structureId}`, null, { params: { user_id: userId } }).then(unwrap),
  removeFavorite:  (structureId, userId) => ssApi.delete(`/navigation/favorites/${structureId}`, { params: { user_id: userId } }).then(unwrap),

  // Per-user history — backend requires `user_id` as a query param
  getHistory:      (userId, limit)       => ssApi.get('/navigation/history/me', { params: { user_id: userId, ...(limit ? { limit } : {}) } }).then(unwrap),
  recordHistory:   (structureId, userId) => ssApi.post(`/navigation/history/${structureId}`, null, { params: { user_id: userId } }).then(unwrap),
};

// ─────────────────────────────────────────────────────────────────────────────
// §6.10 Platform — Notifications, System Params, Business Processes, Version
// ─────────────────────────────────────────────────────────────────────────────
export const ssPlatformAPI = {
  // App version
  getVersion: () => ssApi.get('/version').then(unwrap),

  // In-app notifications (ssusernotifications)
  listNotifications:   (params)    => ssApi.get('/notifications', { params }).then(unwrap),
  markTaken:           (id)        => ssApi.post(`/notifications/${id}/mark-taken`).then(unwrap),
  markAllTaken:        ()          => ssApi.post('/notifications/mark-all-taken').then(unwrap),
  unreadCount:         ()          => ssApi.get('/notifications/unread-count').then(unwrap),

  // System parameters (sssystemparameters)
  listParams:          (params)    => ssApi.get('/system-params', { params }).then(unwrap),
  getParam:            (id)        => ssApi.get(`/system-params/${id}`).then(unwrap),
  createParam:         (d)         => ssApi.post('/system-params', d).then(unwrap),
  updateParam:         (id, d)     => ssApi.put(`/system-params/${id}`, d).then(unwrap),

  // Sequences (sssystemparameters_sequences)
  nextSequence:        (name)      => ssApi.post(`/system-params/sequences/${name}/next`).then(unwrap),
  listSequences:       ()          => ssApi.get('/system-params/sequences').then(unwrap),

  // Business processes
  listProcessTypes:    ()          => ssApi.get('/business-processes/types').then(unwrap),
  listProcesses:       ()          => ssApi.get('/business-processes').then(unwrap),
  startProcess:        (id, d)     => ssApi.post(`/business-processes/${id}/start`, d).then(unwrap),

  // Cache management
  invalidateCache:     (key)       => ssApi.post('/caches/invalidate', { key }).then(unwrap),
};

// ─────────────────────────────────────────────────────────────────────────────
// §6.11 Public contract (consumed by other HHMS modules)
// ─────────────────────────────────────────────────────────────────────────────
export const ssPublicAPI = {
  exists:     (id)       => ssApi.get(`/public/parties/${id}/exists`).then(unwrap),
  summary:    (id)       => ssApi.get(`/public/parties/${id}/summary`).then(unwrap),
  checkPerm:  (d)        => ssApi.post('/public/permissions/check', d).then(unwrap),
  visibility: (mod, uid) => ssApi.get(`/public/visibility/${mod}`, { params: { user_party_id: uid } }).then(unwrap),
  subscribe:  (d)        => ssApi.post('/public/events/subscribe', d).then(unwrap),
};

// ─────────────────────────────────────────────────────────────────────────────
// §6.12 Marketing — public site content config
//
// `getSiteConfig` / `updateSiteConfig` are the admin endpoints, gated by the
// `marketing.config.read` / `marketing.config.write` permissions.  Both
// flow through ssApi so the ETag interceptor handles If-Match for us.
//
// `getPublicSiteConfig` is the unauthenticated read used by the marketing
// site itself.  We hit a fresh axios instance (no auth header) so the
// public endpoint stays anonymous even when an admin is logged in.
// ─────────────────────────────────────────────────────────────────────────────
const _publicAxios = axios.create({
  baseURL: `${BASE_URL}/api/parties-module/v1`,
  timeout: 15000,
});

export const ssMarketingAPI = {
  getSiteConfig:    ()                          => ssApi.get('/marketing/site-config').then(unwrap),
  updateSiteConfig: (payload, expected_version) =>
    ssApi.put('/marketing/site-config', { payload, expected_version }).then(unwrap),
  getPublicSiteConfig: (tenant_id) =>
    _publicAxios.get('/public/marketing/site-config', {
      params: tenant_id != null ? { tenant_id } : {},
    }).then((r) => r.data),
};
